import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { I18nProvider, useI18n } from './i18n/I18nProvider';
import { LoginPage } from './routes/LoginPage';
import { EventsPage } from './routes/EventsPage';
import { EventDetailPage } from './routes/EventDetailPage';
import { KioskPreviewPage } from './routes/KioskPreviewPage';
import { ContactsPage } from './routes/ContactsPage';
import { NewEventPage } from './routes/NewEventPage';
import { PoliciesPage } from './routes/PoliciesPage';
import { AccountPage } from './routes/AccountPage';
import { UsersPage } from './routes/UsersPage';
import { AuditPage } from './routes/AuditPage';
import { KioskPage } from './kiosk/KioskPage';

/**
 * Struttura delle route (§10).
 *
 *   /       → login
 *   /app    → area autenticata
 *   /kiosk  → sessione kiosk associata a device ed evento
 *
 * La protezione qui è di sola navigazione: l'autorizzazione effettiva è
 * applicata dal backend su ogni endpoint (§10.3). Nascondere una schermata non
 * protegge nulla.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Nessuna cache persistente di dati personali nel client operatore.
      gcTime: 5 * 60 * 1000,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const { t } = useI18n();

  if (auth.status === 'loading') return <p style={{ padding: '2rem' }}>{t.common.loading}</p>;
  if (auth.status === 'anonymous') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LoginPage />} />
              <Route
                path="/app"
                element={
                  <RequireAuth>
                    <EventsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/events/new"
                element={
                  <RequireAuth>
                    <NewEventPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/policies"
                element={
                  <RequireAuth>
                    <PoliciesPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/users"
                element={
                  <RequireAuth>
                    <UsersPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/account"
                element={
                  <RequireAuth>
                    <AccountPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/audit"
                element={
                  <RequireAuth>
                    <AuditPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/events/:id"
                element={
                  <RequireAuth>
                    <EventDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/events/:id/preview"
                element={
                  <RequireAuth>
                    <KioskPreviewPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/events/:id/contacts"
                element={
                  <RequireAuth>
                    <ContactsPage />
                  </RequireAuth>
                }
              />
              <Route path="/kiosk" element={<KioskPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </I18nProvider>
  );
}
